import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom';
import {
  search,
  patchById,
  getById,
  searchPA,
  getByIdPA,
  addPA,
  patchByIdPA,
  validatePermissions,
} from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaUsers, 
  FaPlus, 
  FaSearch,
  FaChevronLeft, 
  FaChevronRight,
  FaUser,
  FaEnvelope,
  FaShieldAlt,
  FaTrash,
  FaBuilding,
  FaBriefcase,
  FaComments,
  FaIdCard,
  FaGraduationCap,
  FaExclamationTriangle,
  FaTimes,
  FaCreditCard
} from 'react-icons/fa';
import { Card, Row, Col, Form, Button, Badge, Table, Modal, Spinner } from 'react-bootstrap'
import './Users.css'

function pickNumericPlanId(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function extractPlanIdFromPlanPayload(planPayload) {
  if (planPayload == null || typeof planPayload === 'string') return null
  if (typeof planPayload !== 'object') return null

  let id =
    pickNumericPlanId(planPayload.planId) ??
    pickNumericPlanId(planPayload.plan_id)
  if (id != null) return id

  id =
    pickNumericPlanId(planPayload.plan?.planId) ??
    pickNumericPlanId(planPayload.plan?.plan_id) ??
    pickNumericPlanId(planPayload.data?.planId) ??
    pickNumericPlanId(planPayload.data?.plan_id)
  if (id != null) return id

  const lists = [
    planPayload.planLicenses,
    planPayload.licenses,
    planPayload.memberships,
    planPayload.data?.planLicenses,
  ]
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const row of list) {
      const rowId = pickNumericPlanId(row?.planId) ?? pickNumericPlanId(row?.plan_id)
      if (rowId != null) return rowId
    }
  }
  return null
}

function hasUserAssociatedPlan(user, planPayload) {
  if (user && pickNumericPlanId(user.planId) != null) return true
  if (!planPayload || typeof planPayload !== 'object') return false
  if (planPayload.error) return false
  if (Array.isArray(planPayload.planLicenses) && planPayload.planLicenses.length > 0) return true
  return extractPlanIdFromPlanPayload(planPayload) != null
}

function resolvePlanDisplayName(user, planPayload, planDefinition) {
  if (planDefinition?.planName) return planDefinition.planName
  if (user?.planName) return user.planName
  if (planPayload?.planName) return planPayload.planName
  if (planPayload?.plan?.planName) return planPayload.plan.planName
  const first = Array.isArray(planPayload?.planLicenses) ? planPayload.planLicenses[0] : null
  if (first?.planName) return first.planName
  return null
}

function resolveLicenseCount(planPayload) {
  if (!planPayload || typeof planPayload !== 'object') return null
  if (Array.isArray(planPayload.planLicenses)) return planPayload.planLicenses.length
  const n = pickNumericPlanId(planPayload.licensesNumber ?? planPayload.licensesTotal)
  return n != null ? n : null
}

/** IDs de membresía ligados al usuario (respuesta del servicio `plan/:userId`). */
function collectMembershipIdsFromPlanPayload(planPayload) {
  if (!planPayload || typeof planPayload !== 'object' || planPayload.error) return []
  const ids = new Set()
  if (planPayload.membershipId != null && planPayload.membershipId !== '') {
    ids.add(planPayload.membershipId)
  }
  if (Array.isArray(planPayload.planLicenses)) {
    for (const row of planPayload.planLicenses) {
      const mid = row?.membershipId
      if (mid != null && mid !== '') ids.add(mid)
    }
  }
  if (Array.isArray(planPayload.memberships)) {
    for (const row of planPayload.memberships) {
      const mid = row?.membershipId ?? row?.id
      if (mid != null && mid !== '') ids.add(mid)
    }
  }
  return [...ids]
}

async function logicalDeleteMemberships(membershipIds, token) {
  for (const membershipId of membershipIds) {
    const membershipRes = await patchByIdPA({
      path: 'memberships',
      id: membershipId,
      data: { deleted: true },
      token,
    })

    const membershipOk =
      membershipRes &&
      typeof membershipRes === 'object' &&
      Object.prototype.hasOwnProperty.call(membershipRes, 'membershipId')

    if (!membershipOk) {
      return {
        ok: false,
        message:
          (typeof membershipRes === 'object' && membershipRes?.message) ||
          (typeof membershipRes === 'string' ? membershipRes : null) ||
          'No se pudo eliminar lógicamente la membresía asociada.',
      }
    }
  }
  return { ok: true }
}

// eslint-disable-next-line
export default function (props) {
  const navigate = useNavigate()
  const { users, authenticatedUser, setUsers, setAlert } = useUserContext()
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [showUserModal, setShowUserModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [userPlanPayload, setUserPlanPayload] = useState(undefined)
  const [userPlanLoading, setUserPlanLoading] = useState(false)
  const [planDefinitionFromApi, setPlanDefinitionFromApi] = useState(null)
  const [planCatalogForAssociate, setPlanCatalogForAssociate] = useState([])
  const [associatePlanId, setAssociatePlanId] = useState('')
  const [associateLicenses, setAssociateLicenses] = useState(1)
  const [isAssociatingPlan, setIsAssociatingPlan] = useState(false)

  const [keywords, setKeywords] = useState('')
  const handleChangeKeywords = (e) => setKeywords(e.target.value)

  window.scrollTo({
    top: 0,
    behavior: 'auto',
  })

  const nextPage = () => {
    if(hasMore){
      setPage(page + 1)
    }
  }

  const prevPage = () => {
      if(page > 1 ){
        setPage(page - 1)
      }
  }

  const handleSearchUser = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await search({ path: 'users', keywords, pageSize: 3, token: authenticatedUser.value })
      setHasMore(response.paging.hasMore)
      setUsers(response.data)
    } catch (error) {
      setAlert({
        message: 'Error al buscar usuarios',
        msgType: 'warning'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleUserStatus = async (userId, status, index) => {
    try {
      let userData = await patchById({ path: 'users',
        id: userId,
        data: {
          active: !status
        }, 
        token: authenticatedUser.value })

        if(Object.prototype.hasOwnProperty.call(userData, 'userId')) {
          users[index].active = !status
          setUsers([...users])
          setAlert({
              message: `Usuario ${status ? 'Desactivado' : 'Activado'} exitosamente`,
              msgType: 'success'
            })
          } else {
            setAlert({
              message: userData.message,
              msgType: 'warning'
            })
          }
      } catch (error) {
        setAlert({
          message: 'Error al cambiar el estado del usuario',
          msgType: 'warning'
        })
      }
    }

    const handleUserDelete = (user) => {
      setUserToDelete(user)
      setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
      if (!userToDelete) return

      setIsDeleting(true)
      try {
        const planPayload = await getByIdPA({
          path: 'plan',
          id: userToDelete.userId,
          token: authenticatedUser.value,
        })

        const membershipIds = collectMembershipIdsFromPlanPayload(planPayload)

        if (membershipIds.length > 0) {
          const deleteResult = await logicalDeleteMemberships(
            membershipIds,
            authenticatedUser.value,
          )
          if (!deleteResult.ok) {
            setAlert({
              message: `${deleteResult.message} El usuario no se eliminó.`,
              msgType: 'warning',
            })
            return
          }
        }

        let userData = await patchById({ path: 'users',
          id: userToDelete.userId,
          data: {
            userEmail: `${userToDelete.userEmail}_DELETED-ON-${Date.now()}`,
            deleted: true,
          }, 
          token: authenticatedUser.value })

        if(Object.prototype.hasOwnProperty.call(userData, 'userId')) {
          setUsers(users.filter((user) => user.userId !== userToDelete.userId ))
          setAlert({
            message: membershipIds.length > 0
              ? 'Membresía(es) y usuario eliminados lógicamente.'
              : 'Usuario eliminado exitosamente',
            msgType: 'success'
          })
          setShowDeleteModal(false)
          setUserToDelete(null)
        } else {
          setAlert({
            message: userData.message,
            msgType: 'warning'
          })
        }
    } catch (error) {
      setAlert({
        message: 'Error al eliminar el usuario',
        msgType: 'warning'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setUserToDelete(null)
    setIsDeleting(false)
  }

  useEffect(() => {
    const getUsers = async () => {
      setIsLoading(true)
      try {
        const response = await search({ path: 'users', keywords, page, token: authenticatedUser.value })
        setHasMore(response.paging.hasMore)
        setUsers(response.data)
      } catch (error) {
        setAlert({
          message: 'Error al cargar usuarios',
          msgType: 'warning'
        })
      } finally {
        setIsLoading(false)
      }
    }

    getUsers()
  }, [page, keywords, authenticatedUser.value])

  const getRoleBadge = (user) => {
    // Si el usuario tiene permisos, usar el primer permiso para determinar el rol
    if (user.permissions && user.permissions.length > 0) {
      const permission = user.permissions[0]
      switch (permission) {
        case 'ADMIN':
          return <Badge bg="danger">Administrador</Badge>
        case 'ANFITRION_ORG':
          return <Badge bg="warning" text="dark">Anfitrión Organizacional</Badge>
        case 'ANFITRION_PER':
          return <Badge bg="warning" text="dark">Anfitrión Personal</Badge>
        case 'ANFITRION':
          return <Badge bg="warning" text="dark">Anfitrión</Badge>
        case 'INVITADO':
          return <Badge bg="info">Invitado</Badge>
        default:
          return <Badge bg="secondary">Usuario</Badge>
      }
    }
    
    // Fallback al rol original si no hay permisos
    const role = user.role?.toLowerCase()
    switch (role) {
      case 'admin':
        return <Badge bg="danger">Administrador</Badge>
      case 'host':
        return <Badge bg="warning" text="dark">Anfitrión</Badge>
      default:
        return <Badge bg="secondary">Usuario</Badge>
    }
  }

  // Función para obtener el switch de estado (solo visual)
  const getStatusSwitch = (active, userId, userIndex) => {
    return (
      <div className="status-switch">
        <input
          type="checkbox"
          id={`status-${userId}`}
          checked={active}
          onChange={() => handleUserStatus(userId, active, userIndex)}
          className="switch-input"
        />
        <label htmlFor={`status-${userId}`} className="switch-label">
          <span className="switch-slider"></span>
        </label>
      </div>
    )
  }

  // Función para obtener el badge de estado (para el modal)
  const getStatusBadge = (active) => {
    return active ? 
      <Badge bg="success">Activo</Badge> : 
      <Badge bg="secondary">Inactivo</Badge>
  }

  const handleUserClick = (user) => {
    setSelectedUser(user)
    setShowUserModal(true)
  }

  const handleCloseModal = () => {
    setShowUserModal(false)
    setSelectedUser(null)
    setUserPlanPayload(undefined)
    setPlanDefinitionFromApi(null)
    setPlanCatalogForAssociate([])
    setAssociatePlanId('')
    setAssociateLicenses(1)
    setUserPlanLoading(false)
    setIsAssociatingPlan(false)
  }

  const canManageUserPlan = useMemo(
    () => validatePermissions(authenticatedUser?.permissions, 'users'),
    [authenticatedUser?.permissions],
  )

  const userHasPlan = useMemo(
    () => hasUserAssociatedPlan(selectedUser, userPlanPayload),
    [selectedUser, userPlanPayload],
  )

  const currentLicenseCount = useMemo(
    () => resolveLicenseCount(userPlanPayload),
    [userPlanPayload],
  )

  const displayedPlanId = useMemo(
    () =>
      pickNumericPlanId(selectedUser?.planId) ??
      extractPlanIdFromPlanPayload(userPlanPayload) ??
      null,
    [selectedUser, userPlanPayload],
  )

  const displayedPlanName = useMemo(() => {
    if (!selectedUser) return null
    const name = resolvePlanDisplayName(selectedUser, userPlanPayload, planDefinitionFromApi)
    if (name) return name
    if (displayedPlanId != null) return `Plan #${displayedPlanId}`
    return null
  }, [selectedUser, userPlanPayload, planDefinitionFromApi, displayedPlanId])

  const canSubmitPlanChange = useMemo(() => {
    if (!associatePlanId || associateLicenses < 1) return false
    const nextPlanId = parseInt(associatePlanId, 10)
    if (!Number.isFinite(nextPlanId)) return false
    if (!userHasPlan) return true
    const planChanged = displayedPlanId == null || nextPlanId !== displayedPlanId
    const licensesChanged =
      currentLicenseCount == null || associateLicenses !== currentLicenseCount
    return planChanged || licensesChanged
  }, [
    associatePlanId,
    associateLicenses,
    userHasPlan,
    displayedPlanId,
    currentLicenseCount,
  ])

  useEffect(() => {
    if (!showUserModal || !selectedUser?.userId || !authenticatedUser?.value) return undefined

    let cancelled = false

    const loadUserPlanContext = async () => {
      setUserPlanLoading(true)
      setUserPlanPayload(undefined)
      setPlanDefinitionFromApi(null)
      setPlanCatalogForAssociate([])
      setAssociatePlanId('')
      setAssociateLicenses(1)

      try {
        const freshUser = await getById({
          path: 'users',
          id: selectedUser.userId,
          token: authenticatedUser.value,
        })
        if (!cancelled && freshUser && typeof freshUser === 'object' && freshUser.userId) {
          setSelectedUser(freshUser)
        }

        const planPayload = await getByIdPA({
          path: 'plan',
          id: selectedUser.userId,
          token: authenticatedUser.value,
        })
        if (cancelled) return
        setUserPlanPayload(planPayload)

        const userForCheck =
          freshUser && typeof freshUser === 'object' && freshUser.userId ? freshUser : selectedUser
        const hasPlan = hasUserAssociatedPlan(userForCheck, planPayload)

        if (hasPlan && !cancelled) {
          const licenseCount = resolveLicenseCount(planPayload)
          if (licenseCount != null && licenseCount >= 1) {
            setAssociateLicenses(licenseCount)
          }
        }

        if (canManageUserPlan) {
          const plansRes = await searchPA({
            path: 'plans',
            keywords: '',
            pageSize: 200,
            page: 1,
            token: authenticatedUser.value,
          })
          if (!cancelled && !plansRes.error && Array.isArray(plansRes.data)) {
            setPlanCatalogForAssociate(plansRes.data.filter((p) => p.active !== false))
          }
        }

        if (hasPlan) {
          const pid =
            pickNumericPlanId(userForCheck?.planId) ?? extractPlanIdFromPlanPayload(planPayload)
          if (pid != null && !cancelled) {
            const planRow = await getByIdPA({
              path: 'plans',
              id: pid,
              token: authenticatedUser.value,
            })
            if (planRow && typeof planRow === 'object' && planRow.planId) {
              setPlanDefinitionFromApi(planRow)
            }
          }
        }
      } finally {
        if (!cancelled) setUserPlanLoading(false)
      }
    }

    loadUserPlanContext()
    return () => {
      cancelled = true
    }
  }, [showUserModal, selectedUser?.userId, authenticatedUser.value, canManageUserPlan])

  const refreshUserPlanInModal = async (userId, fallbackPlanId) => {
    const refreshedUser = await getById({
      path: 'users',
      id: userId,
      token: authenticatedUser.value,
    })
    if (refreshedUser && typeof refreshedUser === 'object' && refreshedUser.userId) {
      setSelectedUser(refreshedUser)
    }
    const planPayload = await getByIdPA({
      path: 'plan',
      id: userId,
      token: authenticatedUser.value,
    })
    setUserPlanPayload(planPayload)
    setAssociatePlanId('')
    const licenseCount = resolveLicenseCount(planPayload)
    if (licenseCount != null && licenseCount >= 1) {
      setAssociateLicenses(licenseCount)
    }
    const uid = refreshedUser?.userId ? refreshedUser : selectedUser
    const pid =
      pickNumericPlanId(uid?.planId) ??
      extractPlanIdFromPlanPayload(planPayload) ??
      fallbackPlanId
    if (pid != null) {
      const planRow = await getByIdPA({
        path: 'plans',
        id: pid,
        token: authenticatedUser.value,
      })
      if (planRow && typeof planRow === 'object' && planRow.planId) {
        setPlanDefinitionFromApi(planRow)
      }
    }
  }

  const handleAssociatePlan = async () => {
    if (!canManageUserPlan || !selectedUser?.userId || !associatePlanId) return
    const planIdNum = parseInt(associatePlanId, 10)
    const licensesNum = parseInt(String(associateLicenses), 10)
    if (!Number.isFinite(planIdNum) || !Number.isFinite(licensesNum) || licensesNum < 1) {
      setAlert({
        message: 'Selecciona un plan y una cantidad de licencias válida (mínimo 1).',
        msgType: 'warning',
      })
      return
    }

    if (userHasPlan && !canSubmitPlanChange) {
      setAlert({
        message: 'Elige un plan distinto o cambia la cantidad de licencias.',
        msgType: 'warning',
      })
      return
    }

    setIsAssociatingPlan(true)
    try {
      if (userHasPlan) {
        const membershipIds = collectMembershipIdsFromPlanPayload(userPlanPayload)
        if (membershipIds.length > 0) {
          const deleteResult = await logicalDeleteMemberships(
            membershipIds,
            authenticatedUser.value,
          )
          if (!deleteResult.ok) {
            setAlert({
              message: deleteResult.message,
              msgType: 'warning',
            })
            return
          }
        }
      }

      const res = await addPA({
        path: 'memberships',
        data: {
          userId: selectedUser.userId,
          planId: planIdNum,
          licensesNumber: licensesNum,
        },
        token: authenticatedUser.value,
      })

      if (Object.prototype.hasOwnProperty.call(res, 'planId')) {
        setAlert({
          message: userHasPlan
            ? 'Plan del usuario actualizado correctamente.'
            : 'Plan asociado al usuario correctamente.',
          msgType: 'success',
        })
        await refreshUserPlanInModal(selectedUser.userId, planIdNum)
      } else {
        setAlert({
          message: typeof res === 'string' ? res : res?.message || 'No se pudo asignar el plan.',
          msgType: 'warning',
        })
      }
    } catch {
      setAlert({
        message: 'Error al asignar el plan. Inténtalo de nuevo.',
        msgType: 'warning',
      })
    } finally {
      setIsAssociatingPlan(false)
    }
  }

  return (
    <div className="users-container">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <FaUsers />
            </div>
            <div className="header-text">
              <h1>Gestión de Usuarios</h1>
              <p>Administra y gestiona los usuarios del sistema</p>
            </div>
          </div>
        </div>

        {/* Actions Card */}
        <Card className="actions-card">
          <Card.Body>
            <Row className="align-items-center">
              <Col lg={4} md={6} sm={12}>
                <Button 
                  variant="primary" 
                  className="add-user-btn"
                  onClick={() => navigate('/usuario')}
                >
                  <FaPlus />
                  Agregar Usuario
                </Button>
              </Col>
              <Col lg={8} md={6} sm={12}>
                <Form onSubmit={handleSearchUser} className="search-form">
                  <Row>
                    <Col md={12} sm={12} className="mb-2">
                      <div className="search-input-container">
                        <FaSearch className="search-icon" />
                        <Form.Control
                          type="text"
                          placeholder="Buscar usuarios por nombre, email o país..."
                          value={keywords}
                          onChange={handleChangeKeywords}
                          className="search-input"
                        />
                      </div>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Users Table Card */}
        <Card className="table-card">
          <Card.Body>
            {isLoading ? (
              <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-3">Cargando usuarios...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state">
                <FaUsers className="empty-icon" />
                <h4>No se encontraron usuarios</h4>
                <p>Intenta con otros términos de búsqueda o agrega un nuevo usuario.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="table-responsive d-none d-lg-block">
                  <Table className="users-table">
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Nombre</th>
                        <th>Correo</th>
                        <th>País</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Registro</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.sort((a, b) => b.userId - a.userId).map((user, index) => (
                        <tr key={user.userId} className="user-row">
                          <td className="user-id">#{user.userId}</td>
                          <td>
                            <button 
                              className="user-name-link"
                              onClick={() => handleUserClick(user)}
                              title="Ver detalles del usuario"
                            >
                              {user.fullName}
                            </button>
                          </td>
                          <td className="user-email">{user.userEmail}</td>
                          <td className="user-country">{user.country || '-'}</td>
                          <td>{getRoleBadge(user)}</td>
                          <td>
                            <div className="status-toggle">
                              {getStatusSwitch(user.active, user.userId, index)}
                            </div>
                          </td>
                          <td className="user-date">{user.createdOn}</td>
                          <td>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleUserDelete(user)}
                              className="delete-btn"
                              title="Eliminar usuario"
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Tablet Table View - Hide some columns */}
                <div className="table-responsive d-none d-md-block d-lg-none">
                  <Table className="users-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Correo</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.sort((a, b) => b.userId - a.userId).map((user, index) => (
                        <tr key={user.userId} className="user-row">
                          <td>
                            <button 
                              className="user-name-link"
                              onClick={() => handleUserClick(user)}
                              title="Ver detalles del usuario"
                            >
                              {user.fullName}
                            </button>
                          </td>
                          <td className="user-email">{user.userEmail}</td>
                          <td>{getRoleBadge(user)}</td>
                          <td>
                            <div className="status-toggle">
                              {getStatusSwitch(user.active, user.userId, index)}
                            </div>
                          </td>
                          <td>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleUserDelete(user)}
                              className="delete-btn"
                              title="Eliminar usuario"
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile Cards View - PrimeReact Style */}
                <div className="users-cards d-md-none">
                  {users.sort((a, b) => b.userId - a.userId).map((user, index) => (
                    <Card key={user.userId} className="user-card">
                      <Card.Body className="user-card-body">
                        <div className="user-card-content">
                          <div className="user-avatar">
                            <FaUser />
                          </div>
                          <div className="user-info">
                            <h6 className="user-name" onClick={() => handleUserClick(user)}>
                              {user.fullName}
                            </h6>
                            <p className="user-email">{user.userEmail}</p>
                          </div>
                          <div className="user-status">
                            <div className="role-column">
                              {getRoleBadge(user)}
                            </div>
                            <div className="status-column">
                              <div className="status-switch">
                                <input
                                  type="checkbox"
                                  id={`status-${user.userId}`}
                                  checked={user.active}
                                  onChange={() => handleUserStatus(user.userId, user.active, index)}
                                  className="switch-input"
                                />
                                <label htmlFor={`status-${user.userId}`} className="switch-label">
                                  <span className="switch-slider"></span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="user-actions">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleUserDelete(user)}
                              className="delete-btn"
                              title="Eliminar usuario"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

              </>
            )}
          </Card.Body>
        </Card>

        {/* Pagination */}
        {users.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-wrapper">
              {/* First Page */}
              <button 
                className={`pagination-nav ${page <= 1 ? 'disabled' : ''}`}
                onClick={() => setPage(1)}
                disabled={page <= 1}
                title="Ir al inicio"
              >
                <FaChevronLeft />
                <FaChevronLeft />
              </button>
              
              {/* Previous Page */}
              <button 
                className={`pagination-nav ${page <= 1 ? 'disabled' : ''}`}
                onClick={prevPage}
                disabled={page <= 1}
                title="Página anterior"
              >
                <FaChevronLeft />
              </button>
              
              {/* Page Numbers */}
              <div className="page-numbers">
                {page > 1 && (
                  <button 
                    className="page-number"
                    onClick={() => setPage(page - 1)}
                  >
                    {page - 1}
                  </button>
                )}
                
                <button className="page-number active">
                  {page}
                </button>
                
                {hasMore && (
                  <button 
                    className="page-number"
                    onClick={() => setPage(page + 1)}
                  >
                    {page + 1}
                  </button>
                )}
              </div>
              
              {/* Next Page */}
              <button 
                className={`pagination-nav ${!hasMore ? 'disabled' : ''}`}
                onClick={nextPage}
                disabled={!hasMore}
                title="Página siguiente"
              >
                <FaChevronRight />
              </button>
              
              {/* Last Page */}
              <button 
                className={`pagination-nav ${!hasMore ? 'disabled' : ''}`}
                onClick={() => setPage(page + 1)}
                disabled={!hasMore}
                title="Ir al final"
              >
                <FaChevronRight />
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      <Modal 
        show={showUserModal} 
        onHide={handleCloseModal}
        size="lg"
        centered
        className="user-modal"
      >
        <Modal.Header closeButton className="modal-header">
          <Modal.Title>
            <div className="modal-title-content">
              <div className="user-avatar">
                <FaUser />
              </div>
              <div className="user-title">
                <h4>{selectedUser?.fullName}</h4>
                <p>{selectedUser?.role}</p>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="modal-body">
          {selectedUser && (
            <div className="user-details">
              <Row>
                <Col lg={6} md={12} className="mb-4">
                  <div className="detail-section">
                    <h6 className="section-title">
                      <FaEnvelope className="section-icon" />
                      Información de Contacto
                    </h6>
                    <div className="detail-item">
                      <span className="detail-label">Correo Electrónico:</span>
                      <span className="detail-value">{selectedUser.userEmail}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">País:</span>
                      <span className="detail-value">{selectedUser.country || 'No especificado'}</span>
                    </div>
                  </div>
                </Col>

                <Col lg={6} md={12} className="mb-4">
                  <div className="detail-section">
                    <h6 className="section-title">
                      <FaShieldAlt className="section-icon" />
                      Información del Sistema
                    </h6>
                    <div className="detail-item">
                      <span className="detail-label">ID de Usuario:</span>
                      <span className="detail-value">#{selectedUser.userId}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Estado:</span>
                      <span className="detail-value">
                        {getStatusBadge(selectedUser.active)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Fecha de Registro:</span>
                      <span className="detail-value">{selectedUser.createdOn}</span>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col lg={12}>
                  <div className="detail-section user-plan-detail-section">
                    <h6 className="section-title">
                      <FaCreditCard className="section-icon" />
                      Plan de suscripción
                    </h6>
                    {userPlanLoading ? (
                      <div className="user-plan-loading d-flex align-items-center gap-2 py-2">
                        <Spinner animation="border" size="sm" />
                        <span className="text-muted">Cargando información del plan…</span>
                      </div>
                    ) : (
                      <>
                        {userHasPlan && (
                          <div className="user-plan-associated mb-3">
                            <p className="text-muted small mb-2">Plan actual</p>
                            <div className="detail-item">
                              <span className="detail-label">Plan:</span>
                              <span className="detail-value">{displayedPlanName || '—'}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">ID del plan:</span>
                              <span className="detail-value">
                                {displayedPlanId != null ? `#${displayedPlanId}` : '—'}
                              </span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Licencias (cupos generados):</span>
                              <span className="detail-value">
                                {currentLicenseCount ?? '—'}
                              </span>
                            </div>
                          </div>
                        )}

                        {!userHasPlan && !canManageUserPlan && (
                          <p className="text-muted mb-0">Este usuario no tiene un plan asociado.</p>
                        )}

                        {canManageUserPlan && (
                          <div className="user-plan-associate-form">
                            {userHasPlan && (
                              <p className="text-muted small mb-2">
                                Asignar otro plan (se eliminarán lógicamente las membresías actuales y se creará una nueva).
                              </p>
                            )}
                            <Form.Group className="mb-3">
                              <Form.Label>
                                {userHasPlan ? 'Nuevo plan' : 'Seleccionar plan'}
                              </Form.Label>
                              <Form.Select
                                value={associatePlanId}
                                onChange={(e) => setAssociatePlanId(e.target.value)}
                                disabled={isAssociatingPlan}
                              >
                                <option value="">
                                  {userHasPlan ? 'Seleccione el nuevo plan…' : 'Seleccione un plan…'}
                                </option>
                                {planCatalogForAssociate.map((p) => (
                                  <option key={p.planId} value={String(p.planId)}>
                                    {p.planName} (ID {p.planId}
                                    {p.maxGuests != null ? ` · máx. invitados ${p.maxGuests}` : ''})
                                    {userHasPlan && displayedPlanId === p.planId ? ' — actual' : ''}
                                  </option>
                                ))}
                              </Form.Select>
                              {planCatalogForAssociate.length === 0 && (
                                <Form.Text className="text-muted">
                                  No hay planes activos disponibles o no se pudieron cargar.
                                </Form.Text>
                              )}
                            </Form.Group>
                            <Form.Group className="mb-3">
                              <Form.Label>Cantidad de licencias</Form.Label>
                              <Form.Control
                                type="number"
                                min={1}
                                value={associateLicenses}
                                onChange={(e) => {
                                  const raw = parseInt(e.target.value, 10)
                                  setAssociateLicenses(Number.isFinite(raw) && raw >= 1 ? raw : 1)
                                }}
                                disabled={isAssociatingPlan}
                              />
                              <Form.Text className="text-muted">
                                Se envía como licensesNumber junto con el planId al crear la membresía.
                              </Form.Text>
                            </Form.Group>
                            <div className="mt-3">
                              <Button
                                variant="primary"
                                type="button"
                                disabled={
                                  userPlanLoading ||
                                  !canSubmitPlanChange ||
                                  isAssociatingPlan
                                }
                                onClick={handleAssociatePlan}
                              >
                                {isAssociatingPlan ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {userHasPlan ? 'Actualizando…' : 'Asociando…'}
                                  </>
                                ) : userHasPlan ? (
                                  'Cambiar plan'
                                ) : (
                                  'Asociar plan'
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Additional Information */}
              {(selectedUser.organization || selectedUser.position || selectedUser.comment || selectedUser.licenseCode || selectedUser.specialty) && (
                <Row>
                  <Col lg={12}>
                    <div className="detail-section">
                      <h6 className="section-title">
                        <FaBuilding className="section-icon" />
                        Información Adicional
                      </h6>
                      <Row>
                        {selectedUser.organization && (
                          <Col lg={6} md={12} className="mb-3">
                            <div className="detail-item">
                              <span className="detail-label">
                                <FaBuilding className="detail-icon" />
                                Organización:
                              </span>
                              <span className="detail-value">{selectedUser.organization}</span>
                            </div>
                          </Col>
                        )}
                        
                        {selectedUser.position && (
                          <Col lg={6} md={12} className="mb-3">
                            <div className="detail-item">
                              <span className="detail-label">
                                <FaBriefcase className="detail-icon" />
                                Cargo:
                              </span>
                              <span className="detail-value">{selectedUser.position}</span>
                            </div>
                          </Col>
                        )}
                        
                        {selectedUser.licenseCode && (
                          <Col lg={6} md={12} className="mb-3">
                            <div className="detail-item">
                              <span className="detail-label">
                                <FaIdCard className="detail-icon" />
                                Código de Licencia:
                              </span>
                              <span className="detail-value">{selectedUser.licenseCode}</span>
                            </div>
                          </Col>
                        )}
                        
                        {selectedUser.specialty && (
                          <Col lg={6} md={12} className="mb-3">
                            <div className="detail-item">
                              <span className="detail-label">
                                <FaGraduationCap className="detail-icon" />
                                Especialidad:
                              </span>
                              <span className="detail-value">{selectedUser.specialty}</span>
                            </div>
                          </Col>
                        )}
                        
                        {selectedUser.comment && (
                          <Col lg={12}>
                            <div className="detail-item">
                              <span className="detail-label">
                                <FaComments className="detail-icon" />
                                Comentarios:
                              </span>
                              <span className="detail-value">{selectedUser.comment}</span>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer className="modal-footer">
          <Button variant="outline-secondary" onClick={handleCloseModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmación de Eliminación */}
      <Modal 
        show={showDeleteModal} 
        onHide={cancelDelete}
        centered
        backdrop="static"
        keyboard={false}
        className="delete-confirm-modal"
      >
        <Modal.Header className="delete-modal-header">
          <div className="modal-icon-container">
            <FaExclamationTriangle className="modal-warning-icon" />
          </div>
          <div className="modal-title-container">
            <Modal.Title className="modal-title">Confirmar Eliminación</Modal.Title>
            <p className="modal-subtitle">Esta acción no se puede deshacer</p>
          </div>
          <Button 
            variant="link" 
            onClick={cancelDelete}
            className="modal-close-btn"
            disabled={isDeleting}
          >
            <FaTimes />
          </Button>
        </Modal.Header>
        
        <Modal.Body className="delete-modal-body">
          <div className="user-preview">
            <h6 className="preview-label">Usuario a eliminar:</h6>
            <div className="user-content">
              <p className="user-text">
                <strong>ID:</strong> #{userToDelete?.userId}
              </p>
              <p className="user-text">
                <strong>Nombre:</strong> {userToDelete?.fullName}
              </p>
              <p className="user-text">
                <strong>Email:</strong> {userToDelete?.userEmail}
              </p>
              <p className="user-text">
                <strong>País:</strong> {userToDelete?.country || 'No especificado'}
              </p>
            </div>
          </div>
          
          <div className="warning-message">
            <FaExclamationTriangle className="warning-icon" />
            <p>¿Estás seguro de que quieres eliminar este usuario? Esta acción es permanente y no se puede deshacer.</p>
            <p className="text-muted small mb-0">
              Si el usuario tiene plan o licencias, primero se marcarán como eliminadas sus membresías en el servicio de suscripciones y después el usuario.
            </p>
          </div>
        </Modal.Body>
        
        <Modal.Footer className="delete-modal-footer">
          <Button 
            variant="outline-secondary" 
            onClick={cancelDelete}
            className="cancel-delete-btn"
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
            className="confirm-delete-btn"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Eliminando...
              </>
            ) : (
              'Eliminar Usuario'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
